
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Camera, 
  Share, 
  User, 
  Users, 
  Flame, 
  Trophy, 
  Mail,
  Edit
} from 'lucide-react';
import { ProfilePhotoModal } from '../components/ProfilePhotoModal';
import { InviteFriendsModal } from '../components/InviteFriendsModal';

const Profile = () => {
  const { user, updateUserPhoto } = useAuth();
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-bauhaus-green">Please login to view your profile</p>
        </div>
        <BottomNav />
      </div>
    );
  }
  
  const handlePhotoUpdate = (photoUrl: string) => {
    updateUserPhoto(photoUrl);
    setPhotoModalOpen(false);
  };
  
  // Mock streaks data (in a real app, this would come from the backend)
  const streakDays = 7;
  const hasSubmittedToday = true;
  
  return (
    <div className="min-h-screen flex flex-col bg-bauhaus-offwhite">
      <Header />
      
      <main className="flex-1 container max-w-md mx-auto p-4 pt-6">
        {/* Profile Header */}
        <div className="bg-bauhaus-offwhite border-2 border-bauhaus-green rounded-lg shadow-sm p-6 mb-4">
          <div className="flex items-center">
            <div className="relative">
              <Avatar className="h-20 w-20 border-2 border-bauhaus-green">
                {user.photoUrl ? (
                  <AvatarImage src={user.photoUrl} alt={user.username} />
                ) : (
                  <AvatarFallback className="bg-bauhaus-cream text-bauhaus-darkgreen border-2 border-bauhaus-darkgreen">
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                )}
              </Avatar>
              <Button 
                size="icon" 
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-bauhaus-green text-bauhaus-offwhite shadow-sm"
                onClick={() => setPhotoModalOpen(true)}
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="ml-5">
              <h2 className="text-xl font-bold text-bauhaus-darkgreen">{user.username}</h2>
              <p className="text-bauhaus-darkgreen/80 text-sm">{user.email}</p>
              <div className="flex mt-1 space-x-2">
                <Badge className="bg-bauhaus-green text-bauhaus-offwhite">
                  <Trophy className="h-3 w-3 mr-1" />
                  {user.points} pts
                </Badge>
                <Badge className="bg-bauhaus-green text-bauhaus-offwhite">
                  <Flame className="h-3 w-3 mr-1" />
                  {streakDays} days
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        {/* Streak Info */}
        <div className="bg-bauhaus-offwhite border-2 border-bauhaus-green rounded-lg shadow-sm p-4 mb-4">
          <h3 className="text-md font-medium mb-3 flex items-center text-bauhaus-darkgreen">
            <Flame className="h-5 w-5 text-bauhaus-darkgreen mr-2" />
            Your Streak
          </h3>
          
          <div className="flex items-center mb-3">
            <div className="flex-1 h-2 bg-bauhaus-cream rounded-full overflow-hidden">
              <div 
                className="h-full bg-bauhaus-green rounded-full"
                style={{ width: `${(streakDays % 7) / 7 * 100}%` }}
              ></div>
            </div>
            <span className="text-sm text-bauhaus-darkgreen/80 ml-3">
              {7 - (streakDays % 7)} days to badge
            </span>
          </div>
          
          <div className="text-sm">
            {hasSubmittedToday ? (
              <p className="text-bauhaus-darkgreen font-medium">
                ✓ You've submitted today! Come back tomorrow to continue your streak.
              </p>
            ) : (
              <p className="text-bauhaus-darkgreen font-medium">
                Submit a photo today to maintain your streak!
              </p>
            )}
          </div>
        </div>
        
        {/* Badges */}
        <div className="bg-bauhaus-offwhite border-2 border-bauhaus-green rounded-lg shadow-sm p-4 mb-4">
          <h3 className="text-md font-medium mb-3 flex items-center text-bauhaus-darkgreen">
            <Trophy className="h-5 w-5 text-bauhaus-darkgreen mr-2" />
            Your Badges
          </h3>
          
          <div className="grid grid-cols-4 gap-2">
            {/* Only show earned badges as colored, the rest are gray */}
            <div className="flex flex-col items-center">
              <div className="bg-bauhaus-green text-bauhaus-offwhite p-2 rounded-full">
                <Flame className="h-6 w-6" />
              </div>
              <span className="text-xs mt-1 text-bauhaus-darkgreen">7-day Streak</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-bauhaus-green text-bauhaus-offwhite p-2 rounded-full">
                <Trophy className="h-6 w-6" />
              </div>
              <span className="text-xs mt-1 text-bauhaus-darkgreen">1st Place</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-bauhaus-cream p-2 rounded-full">
                <Users className="h-6 w-6 text-bauhaus-darkgreen/40" />
              </div>
              <span className="text-xs mt-1 text-bauhaus-darkgreen/40">5 Friends</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-bauhaus-cream p-2 rounded-full">
                <Camera className="h-6 w-6 text-bauhaus-darkgreen/40" />
              </div>
              <span className="text-xs mt-1 text-bauhaus-darkgreen/40">30 Photos</span>
            </div>
          </div>
        </div>
        
        {/* Invite Friends */}
        <Button 
          className="w-full bg-bauhaus-green hover:bg-bauhaus-green/90 text-bauhaus-offwhite" 
          onClick={() => setInviteModalOpen(true)}
        >
          <Share className="h-4 w-4 mr-2" />
          Invite Friends (+100 points)
        </Button>
      </main>
      
      <BottomNav />
      
      <ProfilePhotoModal 
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        onPhotoSelected={handlePhotoUpdate}
      />
      
      <InviteFriendsModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />
    </div>
  );
};

export default Profile;
